import {
  Badge,
  Button,
  cn,
  ConfirmableDeleteButton,
  Icon,
  Input,
  SearchableCheckboxList,
  Select,
  Text,
} from '@risksmart-app/atomic-ui';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { CONDITIONAL_OPERATOR_LABELS } from '../config';
import type { ConditionalLogicOption } from '../types';

const SINGLE_VALUE_OPERATORS = new Set(['!=', '!:']);

export const ConditionalLogicRuleBuilder = ({
  fieldOptions,
  getValueOptions,
  currentEditingId,
  disallowedCycleCandidates,
  description,
  errorMessage,
  locked = false,
}: {
  fieldOptions: ConditionalLogicOption[];
  getValueOptions?: (fieldValue: string) => ConditionalLogicOption[];
  currentEditingId?: string;
  disallowedCycleCandidates?: Set<string>;
  description?: string;
  errorMessage?: string | null;
  /**
   * Locks the whole builder read-only: no field/value editing, no rule removal,
   * no "Add rule" button. Used for strict-replace read-only default conditions.
   * Merge mode does NOT set this — it locks individual rows via each rule's
   * `locked` flag instead, leaving "Add rule" and customer rows editable.
   */
  locked?: boolean;
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditionalLogicRules',
  });

  const filteredFieldOptions = fieldOptions.filter(
    (opt) =>
      opt.value !== currentEditingId &&
      // Undefined → allow; only an explicit false rules a field out.
      opt.allowAsSource !== false
  );

  const showOperationSelect = fields.length >= 2;

  return (
    <div className={cn('flex flex-col gap-4 pt-4')}>
      <Text preset={'body-md'} className={cn('font-semibold m-0')}>
        {description ??
          'Show this section based on the output from another field'}
      </Text>
      {locked ? (
        <Text preset={'body-sm'} className={cn('text-muted-foreground')}>
          {'This rule is managed by RiskSmart and cannot be changed.'}
        </Text>
      ) : null}
      {!locked && showOperationSelect ? (
        <Controller
          control={control}
          name={'conditionalLogicOperation'}
          render={({ field: operationField }) => (
            <div className={cn('flex items-center gap-3')}>
              <Text preset={'body-sm'} className={'font-semibold shrink-0'}>
                {'Show when'}
              </Text>
              <Select
                items={[
                  { value: 'and', label: 'All rules match (AND)' },
                  { value: 'or', label: 'Any rule matches (OR)' },
                ]}
                value={operationField.value ?? 'and'}
                onValueChange={operationField.onChange}
              />
            </div>
          )}
        />
      ) : null}
      {fields.map((rule, index) => {
        const selectedIfField = watch(`conditionalLogicRules.${index}.ifField`);
        const selectedOperator =
          watch(`conditionalLogicRules.${index}.operator`) ?? '=';
        // A row is read-only when the whole builder is locked (strict-replace)
        // or when this individual rule is a locked default (merge mode).
        const rowLocked =
          locked || watch(`conditionalLogicRules.${index}.locked`) === true;
        const valueOptions = getValueOptions?.(selectedIfField) ?? [];
        const selectedFieldMeta = filteredFieldOptions.find(
          (opt) => opt.value === selectedIfField
        );
        const isFreeText = selectedFieldMeta?.freeText === true;
        const operatorOptions = selectedFieldMeta?.operatorOptions;
        const showOperatorSelect =
          operatorOptions !== undefined && operatorOptions.length > 1;
        const isSingleValueOperator =
          SINGLE_VALUE_OPERATORS.has(selectedOperator);

        const allSelectedIfFields = fields
          .map((_, i) => watch(`conditionalLogicRules.${i}.ifField`))
          .filter(Boolean);

        // Keep the currently-selected value even if it'd otherwise be hidden,
        // so a stale form value never renders as an empty Select. The schema
        // backstop still catches any cycle the user submits.
        const availableFieldOptions = filteredFieldOptions.filter(
          (opt) =>
            opt.value === selectedIfField ||
            (!allSelectedIfFields.includes(opt.value) &&
              !disallowedCycleCandidates?.has(opt.value))
        );

        const operatorLabel =
          CONDITIONAL_OPERATOR_LABELS[selectedOperator] ?? 'Is equal to';

        const ruleError = (
          errors.conditionalLogicRules as Record<string, unknown>[] | undefined
        )?.[index];

        return (
          <div
            key={rule.id}
            data-slot={'conditional-logic-rule'}
            className={cn(
              'rounded-lg border p-4 flex flex-col gap-3 bg-neutral-minimal',
              ruleError ? 'border-destructive' : 'border-neutral-border'
            )}
          >
            <div className={cn('flex items-center justify-between')}>
              <div className={cn('flex items-center gap-2')}>
                <Text preset={'body-md'} className={'font-semibold'}>
                  {`Rule ${index + 1}`}
                </Text>
                {rowLocked ? (
                  <Badge variant={'neutral'} size={'sm'} border={true}>
                    {'Managed by RiskSmart'}
                  </Badge>
                ) : null}
              </div>
              {rowLocked ? null : (
                <ConfirmableDeleteButton
                  onConfirm={() => remove(index)}
                  size={'icon'}
                />
              )}
            </div>
            <Controller
              control={control}
              name={`conditionalLogicRules.${index}.ifField`}
              render={({ field }) => (
                <div className={cn('flex items-center gap-3')}>
                  <Text
                    preset={'body-sm'}
                    className={'font-semibold shrink-0 w-[60px]'}
                  >
                    {'If'}
                  </Text>
                  <Select
                    items={[
                      {
                        label:
                          availableFieldOptions.length > 0
                            ? 'Pick a field'
                            : 'No valid fields to pick from',
                        value: null,
                      },
                      ...availableFieldOptions,
                    ]}
                    value={field.value || undefined}
                    onValueChange={(v) => {
                      field.onChange(v);
                      setValue(`conditionalLogicRules.${index}.values`, []);
                      // Reset operator to the default for the newly-selected field
                      const newFieldMeta = filteredFieldOptions.find(
                        (opt) => opt.value === v
                      );
                      const defaultOperator =
                        newFieldMeta?.operatorOptions?.[0]?.value ?? '=';
                      setValue(
                        `conditionalLogicRules.${index}.operator`,
                        defaultOperator
                      );
                    }}
                    disabled={rowLocked || availableFieldOptions.length === 0}
                  />
                </div>
              )}
            />
            {selectedIfField && showOperatorSelect ? (
              <Controller
                control={control}
                name={`conditionalLogicRules.${index}.operator`}
                render={({ field: operatorField }) => (
                  <div className={cn('flex items-center gap-3')}>
                    <Text
                      preset={'body-sm'}
                      className={'font-semibold shrink-0 w-[60px]'}
                    >
                      {''}
                    </Text>
                    <Select
                      items={operatorOptions}
                      value={operatorField.value || '='}
                      onValueChange={(v) => {
                        operatorField.onChange(v);
                        setValue(`conditionalLogicRules.${index}.values`, []);
                      }}
                      disabled={rowLocked}
                    />
                  </div>
                )}
              />
            ) : null}
            {selectedIfField && isFreeText ? (
              <Controller
                control={control}
                name={`conditionalLogicRules.${index}.values`}
                render={({ field: valuesField }) => (
                  <div className={cn('flex items-center gap-3')}>
                    {!showOperatorSelect ? (
                      <Text
                        preset={'body-sm'}
                        className={'font-semibold shrink-0 w-[60px]'}
                      >
                        {operatorLabel}
                      </Text>
                    ) : (
                      <div className={cn('shrink-0 w-[60px]')} />
                    )}
                    <Input
                      value={(valuesField.value ?? [])[0] ?? ''}
                      onChange={(e) =>
                        valuesField.onChange(
                          e.target.value === '' ? [] : [e.target.value]
                        )
                      }
                      placeholder={'Enter value'}
                      disabled={rowLocked}
                    />
                  </div>
                )}
              />
            ) : null}
            {selectedIfField && !isFreeText && valueOptions.length > 0 ? (
              <Controller
                control={control}
                name={`conditionalLogicRules.${index}.values`}
                render={({ field: valuesField }) => {
                  if (isSingleValueOperator) {
                    // != and !: → single-value Select
                    return (
                      <div className={cn('flex items-center gap-3')}>
                        <div className={cn('shrink-0 w-[60px]')} />
                        <Select
                          items={[
                            { label: 'Pick a value', value: null },
                            ...valueOptions,
                          ]}
                          value={(valuesField.value ?? [])[0] || undefined}
                          onValueChange={(v) =>
                            valuesField.onChange(v ? [v] : [])
                          }
                          disabled={rowLocked}
                        />
                      </div>
                    );
                  }

                  // = and : → multi-value searchable, virtualised checkbox picker
                  return (
                    <div className={cn('flex items-start gap-3')}>
                      {!showOperatorSelect ? (
                        <Text
                          preset={'body-sm'}
                          className={'font-semibold shrink-0 w-[60px] mt-4'}
                        >
                          {operatorLabel}
                        </Text>
                      ) : (
                        <div className={cn('shrink-0 w-[60px]')} />
                      )}
                      <div
                        className={cn(
                          'flex-1 min-w-0 rounded-md border border-neutral-border p-3 flex flex-col gap-2 bg-neutral'
                        )}
                      >
                        <Text preset={'body-md'}>
                          {'Select trigger inputs:'}
                        </Text>
                        <SearchableCheckboxList
                          options={valueOptions}
                          value={valuesField.value ?? []}
                          onChange={valuesField.onChange}
                          disabled={rowLocked}
                          searchPlaceholder={'Search trigger inputs'}
                          testId={`conditional-value-search-${index}`}
                        />
                      </div>
                    </div>
                  );
                }}
              />
            ) : null}
            {ruleError ? (
              <Text preset={'body-sm'} className={cn('text-destructive pt-4')}>
                {'This rule is invalid'}
              </Text>
            ) : null}
          </div>
        );
      })}
      {locked ? null : (
        <Button
          style={'dashed-fill'}
          radius={'xl'}
          className={'w-full'}
          onClick={() =>
            append({ ifField: '', operator: '=', values: [], locked: false })
          }
        >
          <Icon name={'plus'} size={'sm'} />
          {'Add rule'}
        </Button>
      )}
      {errorMessage ? (
        <Text preset={'body-sm'} className={cn('text-destructive')}>
          {errorMessage}
        </Text>
      ) : null}
    </div>
  );
};
